import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { MeasureUnitCode } from './MeasureUnitCode';
import { MeasureUnitInfo } from './MeasureUnitInfo';
import { MeasureUnitConfig } from './MeasureUnitConfig';
import { TenantConfigContext, TenantConfigUtil } from 'context/TenantConfigContext';

export const useMeasureFormatter = () => {
  const intl = useIntl();
  const tenantConfig = useContext(TenantConfigContext);

  const processInput = (input: number, decimalPlaces: number, config: MeasureUnitConfig) => {
    let toAdd = '';

    let value = input;
    let decimalPlacesFormat = decimalPlaces;
    if (input !== Infinity && input !== -Infinity) {
      const refValue = !config.refValue && config.refValue !== 0 ? input : config.refValue;
      if (Math.abs(refValue) >= 1e6) {
        toAdd = 'M';
        value = input / 1000000.0;
        decimalPlacesFormat = 3;
      } else if (Math.abs(refValue) >= 1e3) {
        if (config.shortenThousands) {
          toAdd = 'k'; // or if german?
          value = input / 1000.0;
          decimalPlacesFormat = 3;
        } else {
          decimalPlacesFormat = 0;
        }
      }
    }

    if (!!config.decimalPlaces || config.decimalPlaces === 0) {
      decimalPlacesFormat = config.decimalPlaces;
    }

    const minDecimalPlaces = config.removeDecimalZeros ? 0 : decimalPlacesFormat;

    return (
      intl.formatNumber(value, {
        minimumFractionDigits: minDecimalPlaces,
        maximumFractionDigits: decimalPlacesFormat,
        signDisplay: config.hasSign ? 'exceptZero' : undefined,
      }) + toAdd
    );
  };

  const measureFormatter: MeasureFormatter = (
    value: number | undefined,
    unit: MeasureUnitCode | MeasureUnitInfo,
    config?: MeasureUnitConfig,
    rate?: number
  ) => {
    const usedConfig = config ?? MeasureUnitConfig.defaultConfig;

    const decimalPlaces = !usedConfig.decimalPlaces && usedConfig.decimalPlaces !== 0 ? 2 : usedConfig.decimalPlaces;

    const unitInfo: MeasureUnitInfo | null = typeof unit === 'string' ? null : unit;
    const unitCode = unitInfo?.code ?? unit;

    if (unitCode === MeasureUnitCode.NONE) {
      return `${value}`;
    }

    if (!value && value !== 0) {
      return '-';
    }

    if (unitCode === MeasureUnitCode.QUANTITY) {
      return processInput(value, 0, usedConfig);
    }

    if (unitCode === MeasureUnitCode.PERCENT) {
      return `${processInput(value * 100, decimalPlaces, usedConfig)} ${
        usedConfig.isDiff
          ? intl.formatMessage({
              id: 'measure.format.percentage-points',
              defaultMessage: 'p.P.',
            })
          : '%'
      }`;
    }

    if (unitCode === MeasureUnitCode.CURRENCY) {
      const formattedValue = processInput(value, decimalPlaces, usedConfig);
      const symbol = TenantConfigUtil.getCurrencySymbol(
        (unitInfo?.isOriginalCurrency ? unitInfo.currencyCode : null) ?? tenantConfig.currencyCode
      );

      if (intl.locale.startsWith('en')) {
        return usedConfig.hasSign && value > 0
          ? formattedValue.charAt(0) + (symbol ? `${symbol} ` : '') + formattedValue.substring(1)
          : (symbol ? `${symbol} ` : '') + formattedValue;
      }
      return formattedValue + (symbol ? ` ${symbol}` : '');
    }

    if (unitCode === MeasureUnitCode.FLOAT) {
      return processInput(value, 2, usedConfig);
      // hint: these two units are not needed for now - lets migrate them, when they become needed
      // } else if (unitCode === 'TIME') {
      //    return this.durationPipe.transform(input * 1000);
      // } else if (unitCode === 'DATE') {
      //    return this.datePipe.transform(new Date(input), this.timeService.getDateFormat());
    }

    if (unitCode === MeasureUnitCode.RATE) {
      if (rate !== undefined) {
        return `${processInput(rate * 100, 2, usedConfig)}%`;
      }

      return processInput(value, 0, usedConfig);
    }
    return `${value}`;
  };

  return {
    measureFormatter,
  };
};

export type MeasureFormatter = (
  value: number | undefined,
  unit: MeasureUnitCode | MeasureUnitInfo,
  config?: MeasureUnitConfig,
  rate?: number
) => string;
